import { useState, useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, UseFormSetValue } from "react-hook-form";
import { useNavigate, useSearchParams, useLocation, NavigateFunction } from "react-router-dom";
import { z } from "zod";
import Modal from "components/modal/Modal";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { DecryptedUser, LeadProvider, RegisterData, RegisterResponse, RegistrationRoleType, useAddPhoneMfaDeviceMutation, useGetDecryptUserQuery, useGetUserDetailsQuery, usePostRegistrationMutation } from "./registrationService";
import * as Constant from "utilities/Constant";
import { emailAddress, phone, password_input, firstname, lastname, loanpurpose } from "utilities/formSchemas";
import "./registration.scss";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "store";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getEmailAddress, getLeadProvider, getNhDealApplicationId, setCredentials, setEmailAddress, setLeadProvider, setPreferredLanguage, setUserId } from "features/authSlice";
import { getLastName } from "utilities/string";
import { ErrorType } from "utilities/error";
import { Consent } from "components/consent/Consent";
import useDesktopChecker from "hooks/useDesktopChecker";
import {
  setFSUserProperties,
  tagFSLeadContextFromDecrypt,
  trackFSRegistrationPagePhoneLO,
  trackFSRegistrationPageEmailLO,
  FLOW_CONTEXT,
  AUTH_OUTCOME,
  REGISTRATION_RAIL,
  resolveRegistrationRail,
  flowContextForRegistrationRail,
  buildRegistrationFunnelLeadFields,
  isUnknownRegistrationRole,
  mapLeadBorrowerType,
  mapRegistrationOutcome,
  toAuthFailureCode,
  trackFSAuthError,
  trackFSAuthStep,
  trackFSRegistrationPageRendered,
  trackFSRegistrationSubmitted,
  trackFSRegistrationCompleted,
  trackFSRegistrationFailed,
} from "utilities/full-story";
import { useLeadDecryptTracking, useLeadLinkOpened } from "hooks/usePreAuthLeadTracking";
import { useLoProfileCardTracking } from "hooks/useLoProfileCardTracking";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import { setResendCodeDeviceId, setAuthDeviceType } from "features/mfaSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { RegistrationLegalConsent } from "components/registration-legal-consent/Registration-legal-consent";
import Footer from "components/footer/Footer";
import { LoanOfficerDetail, useGetLoanOfficerByLoIdQuery, useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery } from "pages/application/applicationService";
import LoanOfficerCardV2 from "components/v2/loan-officer-card/LoanOfficerCardV2";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";
import { RegistrationInformationPanel } from "pages/registration/RegistrationInformationPanel";
import AuthNativeAppDownloadPanel from "components/v2/auth-native-app-download/AuthNativeAppDownloadPanel";
import AuthSplitLayout from "components/v2/auth-native-app-download/AuthSplitLayout";
import { getPrivacyAndTermsOfUseConsent } from "utilities/consentLegalContent";
// ADO-535683: Spanish invite pre-set
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { getPersistedPreferredLanguage, hasInviteAckDone, markInviteAckDone, markExplicitUserLanguageSelection, setPersistedPreferredLanguage } from "utilities/getPreferredLanguage";
import { syncLangPrefCookie } from "utilities/langPrefCookie";

const formSchema = z.object({
  email: emailAddress,
  firstname: firstname.optional(),
  lastname: lastname.optional(),
  phone: phone.optional(),
  loanpurpose: loanpurpose.optional(),
  password_input,
});

type RegistrationFunnelTrackProps = {
  flow_context: typeof FLOW_CONTEXT.registration | typeof FLOW_CONTEXT.user_gateway;
  registrationRail: typeof REGISTRATION_RAIL.invite | typeof REGISTRATION_RAIL.user_gateway;
  leadProvider?: string | null;
  loanType?: string | null;
  borrowerType?: string | null;
  borrowerRole?: string | null;
};

const REGISTRATION_CELL_PHONE_MESSAGE = "Enter Valid Cell Phone Number";

type RegistrationSuccessDeps = {
  data: RegisterResponse;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  setError: (code: number) => void;
  email: string;
  funnelProps: RegistrationFunnelTrackProps;
  setLocalPhmfapending: (value: boolean) => void;
  setPhoneErrorMessage: (msg: string) => void;
};

const handleRegistrationSuccess = ({
  data,
  dispatch,
  navigate,
  setError,
  email,
  funnelProps,
  setLocalPhmfapending,
  setPhoneErrorMessage,
}: RegistrationSuccessDeps): void => {

  const status = data.status;
  const outcome = mapRegistrationOutcome(status);
  if (outcome) {
    const resolvedRole =
      [data.userRole, funnelProps.borrowerRole].find((role) => role && !isUnknownRegistrationRole(role)) ??
      RegistrationRoleType.Borrower;
    trackFSRegistrationCompleted({
      ...funnelProps,
      borrowerRole: resolvedRole,
      borrowerType: mapLeadBorrowerType(resolvedRole) ?? funnelProps.borrowerType,
      outcome,
      isMfaSkipEnabled: false,
    });
  }
  if (status === "OTP_REQUIRED") {
    trackFSAuthStep(funnelProps.flow_context, AUTH_OUTCOME.otp_required);
    dispatch(setEmailAddress(email));
    dispatch(setUserId(data.id));
    dispatch(setResendCodeDeviceId(data.deviceAuthId));
    dispatch(setAuthDeviceType(data.deviceType ?? ""));
    navigate("/registrationAuth", { replace: true });
    return;
  }
  if (status === "MOBILE_MFA_PENDING") {
    setError(0);
    setPhoneErrorMessage(REGISTRATION_CELL_PHONE_MESSAGE);
    setLocalPhmfapending(true);
    return;
  }
  if (status?.toLowerCase() === "completed") {
    trackFSAuthStep(funnelProps.flow_context, AUTH_OUTCOME.completed);
    setFSUserProperties(email, getLastName(data.name).toLowerCase());
    dispatch(setCredentials({ ...data }));
    navigate("/my-loans", { replace: true });
    return;
  }
  if (status === "DEVICE_SELECTION_REQUIRED") {
    trackFSAuthStep(funnelProps.flow_context, AUTH_OUTCOME.otp_required);
    dispatch(setEmailAddress(email));
    dispatch(setUserId(data.id));
    navigate("/mfa", { replace: true });
    return;
  }
  setError(500);
};

const getSalesChannel = (firstDomainName: string): string => {
  if (firstDomainName === "retail") return "OLA";
  if (firstDomainName === "cd") return "ILA";
  return "Unknown";
};

const renderDesktopLoPanel = (
  loanOfficer: LoanOfficerDetail | null,
  isLoadingLO: boolean,
  onPhoneClick: () => void,
  onEmailClick: () => void,
  onHeadshotError: () => void
) => {
  if (loanOfficer) {
    return (
      <LoanOfficerCardV2
        loInfo={loanOfficer}
        onPhoneClick={onPhoneClick}
        onEmailClick={onEmailClick}
        onHeadshotError={onHeadshotError}
      />
    );
  }

  return <RegistrationInformationPanel />;
};

const handleSubmitError = (
  error: unknown,
  navigate: NavigateFunction,
  setPhoneErrorMessage: (msg: string) => void,
  setPasswordErrorMessage: (msg: string) => void,
  setError: (code: number) => void,
  funnelProps: RegistrationFunnelTrackProps
): void => {
  const err = error as ErrorType;
  trackFSAuthError(funnelProps.flow_context, error);
  trackFSRegistrationFailed({ ...funnelProps, FailureCode: toAuthFailureCode(error) });
  if (err.status === 409) {
    if (err.data?.code === Constant.ErrorCodeInvalidPassword) {
      setPasswordErrorMessage(Constant.RESET_PASSWORD_ERROR);
      return;
    }
    navigate("/", { replace: true });
    return;
  }
  if (err.status === 400 && err.data?.code === Constant.ErrorCodeInvalidMFA) {
    setPhoneErrorMessage(REGISTRATION_CELL_PHONE_MESSAGE);
    return;
  }
  setError(500);
};

const needsPhoneVerification = (isPhoneRequired: boolean | undefined, phmfapending: boolean, phoneNumber: string | undefined): boolean =>
  !!(isPhoneRequired || phmfapending) && !!phoneNumber;

interface LoTrackInfo {
  fullName?: string | null;
  nmlsLicenseNumber?: string | null;
  domains?: Array<{ domainName?: string | null }> | null;
}


const submitRegistrationForm = async (
  formData: RegisterData,
  deps: {
    hasErrors: boolean;
    hasPwdError: boolean;
    isPhoneRequired: boolean | undefined;
    phmfapending: boolean;
    addPhoneMfaDevice: (arg: { email: string; phoneNumber: string }) => { unwrap: () => Promise<unknown> };
    sendRequest: (data: RegisterData) => { unwrap: () => Promise<unknown> };
    navigate: NavigateFunction;
    setPhoneErrorMessage: (msg: string) => void;
    setPasswordErrorMessage: (msg: string) => void;
    setError: (code: number) => void;
    funnelProps: RegistrationFunnelTrackProps;
  }
): Promise<void> => {
  if (deps.hasErrors || deps.hasPwdError) return;
  try {
    if (needsPhoneVerification(deps.isPhoneRequired, deps.phmfapending, formData.phoneNumber)) {
      await deps.addPhoneMfaDevice({ email: formData.emailaddress, phoneNumber: formData.phoneNumber as string }).unwrap();
    }
    await deps.sendRequest(formData).unwrap();
  } catch (error) {
    handleSubmitError(error, deps.navigate, deps.setPhoneErrorMessage, deps.setPasswordErrorMessage, deps.setError, deps.funnelProps);
  }
};

const buildLOTrackPayload = (loanOfficer: LoTrackInfo, lpn?: string | null) => ({
  leadProvider: lpn ?? "Unknown",
  salesChannel: getSalesChannel((loanOfficer?.domains?.[0]?.domainName ?? "").trim().toLowerCase()),
  referringUrl: document.referrer || globalThis.location.href || "Unknown",
  loFullName: loanOfficer.fullName ?? "Unknown",
  loNmls: loanOfficer.nmlsLicenseNumber ?? "Unknown",
  state: "Unknown",
});

const getPixelSrc = (lpn: string | null | undefined, id: string | null | undefined): string => {
  const leadProvider = encodeURIComponent(lpn ?? "Others");
  const inquiryId = encodeURIComponent(id && id !== "00000000-0000-0000-0000-000000000000" ? id : "");
  return `${Constant.TTD_CODE_SNIPPET_URL}/?adv=ffdysrg&ct=0:vf1o9ix&fmt=3&td1=${leadProvider}&td2=&td3=${inquiryId}`;
};

const hasPixelTrigger = (d: unknown, lpn: unknown): boolean => !!(d ?? lpn);

const computeIsPhoneValid = (
  isPhoneRequired: boolean | undefined,
  phmfapending: boolean,
  phoneDigits: string
): boolean => !(isPhoneRequired || phmfapending) || phoneDigits.length === 10;

const getLoLookUpId = (nhDealAppId: string | null | undefined, inquiryId: string): string =>
  nhDealAppId && nhDealAppId !== "00000000-0000-0000-0000-000000000000" ? nhDealAppId : inquiryId;

const RegistrationErrorDisplay = ({ isDesktop }: { isDesktop: boolean }) => (
  <div className="registration__error">
    {isDesktop
      ? Constant.RegistrationError
      : Constant.RegistrationError.map((e) => <p key={e}>{e}</p>)}
  </div>
);

const processDecryptedUser = (
  data: DecryptedUser,
  leadProviderName: string,
  navigate: NavigateFunction,
  dispatch: AppDispatch,
  setValue: UseFormSetValue<z.infer<typeof formSchema>>,
  setIsBtnDisabled: (updater: (prev: boolean) => boolean) => void,
  setIsBonzoSpouse: (value: boolean) => void
): void => {
  const { ex, fn, ln, em, ph, lp, lpn, rt, phmfapending } = data;
  tagFSLeadContextFromDecrypt(data);
  if (ex) {
    navigate("/", { replace: true, state: data });
    return;
  }
  if (lpn) {
    dispatch(setLeadProvider(lpn));
  }
  setIsBtnDisabled((prevState) => !prevState);
  setValue("firstname", fn);
  setValue("lastname", ln);
  setValue("email", em);
  if (!phmfapending) {
    setValue("phone", ph.replaceAll("-", ""));
  }
  setValue("loanpurpose", lp ?? undefined);
  const isBonzoSpousalCoBorrower =
    leadProviderName?.toLowerCase() === LeadProvider.Bonzo.toLowerCase() &&
    rt?.toLowerCase() === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
  setIsBonzoSpouse(isBonzoSpousalCoBorrower);
};

export default function Registration() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useDesktopChecker();
  const encryptedUser = searchParams.get("t")!;
  const email = searchParams.get("email")!;
  const loanId = searchParams.get("loanId") ?? searchParams.get("lid")!;
  const loanOfficerId = searchParams.get("log");
  const dealId = searchParams.get("did")!;
  const prospectId = searchParams.get("pid")!;
  const leadProviderName = searchParams.get("lpn")!;
  const losId = searchParams.get("losId")!;
  const isEncryptedUser = !!encryptedUser;
  const isBonzoUrlParams = !!(email && loanId && leadProviderName);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const isNativeAppDownloadCtaEnabled = !!featureFlag?.nativeAppDownloadCtaEnabled;
  const emailFromStore = useSelector(getEmailAddress);
  const leadProviderFromStore = useSelector(getLeadProvider);
  const nhDealApplicationIdFromStore = useSelector(getNhDealApplicationId);
  const fromUserGateway =
    (location.state as { registrationRail?: string } | null)?.registrationRail === REGISTRATION_RAIL.user_gateway;
  const registrationRail = resolveRegistrationRail({
    route: "registration",
    hasInviteToken: isEncryptedUser || isBonzoUrlParams,
    fromUserGateway,
    hasEmailFromStore: !!emailFromStore,
  });
  const flowContext = flowContextForRegistrationRail(registrationRail);
  const {
    data: decryptedUserDetails,
    isLoading,
    isFetching,
    isSuccess: isDecryptUserSuccess,
    isError: isDecryptUserError,
    error: decryptUserError,
  } = useGetDecryptUserQuery({ encryptedUser, email, loanId, leadProviderName, dealId, losId, prospectId }, { skip: !(isEncryptedUser || isBonzoUrlParams) });

  useLeadLinkOpened("/registration", {
    leadProvider: leadProviderName,
    hasEncryptedToken: isEncryptedUser,
  });
  useLeadDecryptTracking({
    entryRoute: "/registration",
    skip: !(isEncryptedUser || isBonzoUrlParams),
    isLoading,
    isFetching,
    isSuccess: isDecryptUserSuccess,
    isError: isDecryptUserError,
    error: decryptUserError,
    data: decryptedUserDetails ?? null,
    leadProviderFromUrl: leadProviderName,
  });

  const nhDealAppId = decryptedUserDetails?.nhDealAppId ?? nhDealApplicationIdFromStore;
  const inquiryId = decryptedUserDetails?.id ?? "";
  const inviteAckKey = [nhDealAppId, inquiryId, emailFromStore].find((value) => value.trim().length > 0);
  const loLookUpId = getLoLookUpId(nhDealAppId, inquiryId);

  // Fetch by loId first (self-reg / "log" URL param path). If present, skip the
  // nhDealApp/inquiryId lookup entirely to avoid a redundant network call.
  const { data: loDataByLoId = null, isLoading: isLoadingLoByLoId, error: loErrorByLoId } = useGetLoanOfficerByLoIdQuery(
    loanOfficerId ?? "",
    { skip: !loanOfficerId }
  );

  // Skip if: decrypt hasn't succeeded yet, no valid lookup ID exists, or LO data
  const { data: loData = null, isLoading: isLoadingLoByDeal, error: loErrorByDeal } = useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery(loLookUpId,
    { skip: !isDecryptUserSuccess || (!inquiryId && !nhDealAppId) || !!loanOfficerId }
  );

  const rawLoData = loDataByLoId ?? loData;
  const loanOfficer =
    rawLoData?.fullName && rawLoData.fullName !== "" && rawLoData.fullName !== "Call Queue" ? rawLoData : null;
  const isLoadingLO = isLoading || isFetching || isLoadingLoByLoId || isLoadingLoByDeal;
  const loanOfficerError = loErrorByLoId ?? loErrorByDeal;

  const [loHeadshotLoadError, setLoHeadshotLoadError] = useState(false);
  useEffect(() => {
    setLoHeadshotLoadError(false);
  }, [loanOfficer?.photoImageUrl]);

  useLoProfileCardTracking({
    surface: "registration",
    loInfo: loanOfficer,
    isLoading: isLoadingLO,
    error: loanOfficerError,
    headshotLoadError: loHeadshotLoadError,
    dealId: nhDealAppId,
  });

  const {
    data: isPhoneRequired,
    isSuccess: isUserDetailsSuccess,
    isLoading: isUserDetailsLoading,
    isFetching: isPhoneNumberFetching,
  } = useGetUserDetailsQuery(emailFromStore || "", {
    skip: !emailFromStore,
  });

  useEffect(() => {
    if (!decryptedUserDetails) return;
    processDecryptedUser(decryptedUserDetails, leadProviderName, navigate, dispatch, setValue, setIsBtnDisabled, setIsBonzoSpouse);
  }, [decryptedUserDetails]);

  const funnelProps: RegistrationFunnelTrackProps = {
    flow_context: flowContext as RegistrationFunnelTrackProps["flow_context"],
    registrationRail: registrationRail as RegistrationFunnelTrackProps["registrationRail"],
    ...buildRegistrationFunnelLeadFields(decryptedUserDetails, {
      leadProvider: decryptedUserDetails?.lpn ?? leadProviderName ?? leadProviderFromStore,
      borrowerRole: RegistrationRoleType.Borrower,
    }),
  };
  const didTrackVisit = useRef(false);

  useEffect(() => {
    if (didTrackVisit.current) return;
    const inviteReady = !!decryptedUserDetails && !decryptedUserDetails.ex;
    const gatewayReady = registrationRail === REGISTRATION_RAIL.user_gateway && !!emailFromStore;
    if (!inviteReady && !gatewayReady) return;
    if (inviteReady && isLoadingLO) return;
    didTrackVisit.current = true;
    const firstDomainName = (loanOfficer?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
    trackFSRegistrationPageRendered({
      ...funnelProps,
      referringUrl: document.referrer || globalThis.location.href || "Unknown",
      salesChannel: getSalesChannel(firstDomainName),
      loFullName: loanOfficer?.fullName ?? undefined,
      loNmls: loanOfficer?.nmlsLicenseNumber ?? undefined,
      state: "Unknown",
      loCardVisible: !!loanOfficer,
    });
  }, [
    decryptedUserDetails,
    emailFromStore,
    registrationRail,
    loanOfficer,
    isLoadingLO,
    funnelProps.leadProvider,
    funnelProps.loanType,
    funnelProps.borrowerType,
    funnelProps.flow_context,
    funnelProps.registrationRail,
  ]);

  useEffect(() => {
    if (emailFromStore) {
      setValue("email", emailFromStore);
    }
  }, [emailFromStore]);

  // Show Tracking Pixel
  const [showPixel, setShowPixel] = useState(false);
  const pixelReady = hasPixelTrigger(decryptedUserDetails, leadProviderName);
  useEffect(() => {
    if (featureFlag?.ttdCodeSnippetEnabled && !isLoading && !isFetching && pixelReady) {
      setShowPixel(true);
    }
  }, [featureFlag?.ttdCodeSnippetEnabled, isLoading, isFetching, pixelReady]);

  const showModal = !isLoading && !isFetching && !decryptedUserDetails && !emailFromStore;

  const [error, setError] = useState(0);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [hasPwdError, setHasPwdError] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [isBonzoSpouse, setIsBonzoSpouse] = useState(false);
  const [localPhmfapending, setLocalPhmfapending] = useState(false);
  const submittedEmailRef = useRef("");
  const dispatch = useDispatch();
  const [sendRequest, { isSuccess, data, isLoading: isRegistrationLoading, reset: resetRegistration }] = usePostRegistrationMutation();
  const [addPhoneMfaDevice, { isLoading: isAddingMfaDeviceInProgress }] = useAddPhoneMfaDeviceMutation();
  const handledRegistrationResponseRef = useRef<string | null>(null);

  // ADO-535683: Spanish invite pre-set modal
  const [showSpanishModal, setShowSpanishModal] = useState(false);

  // ADO-535683: after page reload triggered by acknowledge, Redux resets to English.
  // Sync Redux from sessionStorage so the language toggle reflects Spanish correctly.
  useEffect(() => {
    if (hasInviteAckDone(inviteAckKey)) {
      const persisted = getPersistedPreferredLanguage();
      if (persisted) {
        dispatch(setPreferredLanguage(persisted));
      }
    }
  }, [dispatch, inviteAckKey]);

  // Show the bilingual acknowledgment modal once when the decrypted token carries Spanish preference
  useEffect(() => {
    if (
      decryptedUserDetails?.pl === "Spanish" &&
      featureFlag?.preferredLanguageEnabled &&
      !hasInviteAckDone(inviteAckKey)
    ) {
      setShowSpanishModal(true);
    }
  }, [decryptedUserDetails?.pl, featureFlag?.preferredLanguageEnabled, inviteAckKey]);

  const handleSpanishAcknowledge = (): void => {
    try {
      // Mark ack BEFORE reload so the guard above skips the modal on the next render
      markInviteAckDone(inviteAckKey);
      // Mark explicit selection so resolveUnauthenticatedDisplayLanguage returns Spanish on /registration
      markExplicitUserLanguageSelection();
      setPersistedPreferredLanguage(Constant.SUPPORTED_LANGUAGES[1]);
      syncLangPrefCookie(Constant.SUPPORTED_LANGUAGES[1]);
      // Reload so TransPerfect picks up the Spanish cookie
      globalThis.location.replace(globalThis.location.href);
    } catch (error) {
      // Log error and do not reload to prevent inconsistent state
      console.error("Failed to acknowledge Spanish preference:", error);
    }
  };

  const handleSpanishCancel = (): void => {
    // Borrower chose English — User DB and Deals are updated by the post-OTP deal-sync in usePreferredLanguage
    markInviteAckDone(inviteAckKey);
    markExplicitUserLanguageSelection();
    setPersistedPreferredLanguage(Constant.DEFAULT_LANGUAGE);
    setShowSpanishModal(false);
  };

  const handleLOPhoneClick = () => {
    if (loanOfficer) trackFSRegistrationPagePhoneLO(buildLOTrackPayload(loanOfficer, decryptedUserDetails?.lpn));
  };

  const handleLOEmailClick = () => {
    if (loanOfficer) trackFSRegistrationPageEmailLO(buildLOTrackPayload(loanOfficer, decryptedUserDetails?.lpn));
  };

  const phmfapending = localPhmfapending || !!decryptedUserDetails?.phmfapending;

  const onSubmit = (data: RegisterData) => {
    submittedEmailRef.current = data.emailaddress;
    if (hasErrors || hasPwdError) {
      return;
    }
    trackFSRegistrationSubmitted({
      ...funnelProps,
      phoneRequired: !!(isPhoneRequired || phmfapending),
      loFullName: loanOfficer?.fullName,
      loNmls: loanOfficer?.nmlsLicenseNumber,
    });
    return submitRegistrationForm(data, {
      hasErrors,
      hasPwdError,
      isPhoneRequired,
      phmfapending,
      addPhoneMfaDevice,
      sendRequest,
      navigate,
      setPhoneErrorMessage,
      setPasswordErrorMessage,
      setError,
      funnelProps,
    });
  };

  useEffect(() => {
    if (!isSuccess || !data) {
      if (!isSuccess) handledRegistrationResponseRef.current = null;
      return;
    }
    const responseKey = `${data.id ?? ""}:${data.status ?? ""}`;
    if (handledRegistrationResponseRef.current === responseKey) return;
    handledRegistrationResponseRef.current = responseKey;

    handleRegistrationSuccess({
      data,
      dispatch,
      navigate,
      setError,
      email: submittedEmailRef.current || emailFromStore || "",
      funnelProps,
      setLocalPhmfapending,
      setPhoneErrorMessage,
    });
    resetRegistration();
  }, [isSuccess, data, dispatch, navigate, emailFromStore, funnelProps, resetRegistration]);

  const ispasswordFieldModified = Boolean(dirtyFields.password_input);
  const hasErrors = !!Object.keys(errors).length;
  const phoneValue = watch("phone");
  const phoneDigits = phoneValue?.replaceAll(/\D/g, "") ?? "";
  const isPhoneValid = computeIsPhoneValid(isPhoneRequired, phmfapending, phoneDigits);

  if (isRegistrationLoading || isUserDetailsLoading || isAddingMfaDeviceInProgress) return <LoadingOverlay text="Loading..." />;

  const appDownloadPanel = isLoadingLO ? null : (
    <AuthNativeAppDownloadPanel
      loanOfficer={loanOfficer}
      onPhoneClick={handleLOPhoneClick}
      onEmailClick={handleLOEmailClick}
      onHeadshotError={() => setLoHeadshotLoadError(true)}
    />
  );

  const registrationPage = (
    <div className={`registration${isNativeAppDownloadCtaEnabled ? " registration--auth-split" : ""}`}>
      {showModal && (
        <Modal
          defaultBody
          defaultBodyLabel="Something went wrong!"
          defaultBodyLabelIcon={regular("triangle-exclamation")}
          defaultBodySubContent={[
            "Please open the email we sent you and click on the link so that we can try again.",
            "We apologize for the inconvenience.",
          ]}
          footer={<></>}
        />
      )}
      <div className="registration__container">
        <div className="registration__form">
          <h1 className="registration__header">{isDesktop ? `Create an account to continue your application` : <>Create an account<br />to continue</>}</h1>
          {!isNativeAppDownloadCtaEnabled && !isDesktop && loanOfficer && (
            <>
              <div className="registration__mobile-lo-invite">
                <p>You&#39;ve been invited to start your application.</p>
              </div>
              <div className="registration__mobile-lo-card">
                <LoanOfficerCard 
                  loInfo={loanOfficer} 
                  hideLabel 
                  onPhoneClick={handleLOPhoneClick}
                  onEmailClick={handleLOEmailClick}
                  onHeadshotError={() => setLoHeadshotLoadError(true)}
                />
              </div>
            </>
          )}
          {!isNativeAppDownloadCtaEnabled && !isDesktop && !loanOfficer && isLoadingLO && (
            <div className="registration__mobile-lo-card">
              <LoanOfficerCard isLoading hideLabel />
            </div>
          )}
          {error == 500 && <RegistrationErrorDisplay isDesktop={isDesktop} />}
          {phoneErrorMessage && <div className="registration__error">{phoneErrorMessage}</div>}
          {passwordErrorMessage && <div className="registration__error">{passwordErrorMessage}</div>}
          <Form
            onSubmit={handleSubmit((data) => {
              void onSubmit({
                emailaddress: data.email,
                password: data.password_input,
                firstname: data.firstname,
                lastname: data.lastname,
                loanPurpose: data.loanpurpose,
                phoneNumber: data.phone,
                referralSource: "",
                referredBy: "someone",
                inquiryId: decryptedUserDetails?.id,
                leadProvider: isBonzoSpouse ? LeadProvider.Bonzo : decryptedUserDetails?.lpn,
                nhDealApplicationId: nhDealApplicationIdFromStore || decryptedUserDetails?.nhDealAppId || undefined,
                userRole: decryptedUserDetails?.rt,
                isTestLoan: decryptedUserDetails?.isTestLoan,
                decryptPhoneNumber: decryptedUserDetails?.ph,
                prospectId: decryptedUserDetails?.prospectId,
              })?.catch(() => {
                setError(500);
              });
            })}
          >
            <div className="row--item">
              <FormInput
                formHandle={register("email")}
                inputProps={{
                  name: "email",
                  placeholder: "Enter Your Email Address",
                  label: "Email",
                  errorMessage: errors.email?.message,
                  isReadOnly: true,
                }}
              />
            </div>
            <div className={!(!isPhoneNumberFetching && isPhoneRequired && isUserDetailsSuccess) && !phmfapending ? "row--hidden" : "row--item"}>
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <PhoneNumberInput
                    onChange={(newValue: string) => {
                      onChange(newValue);
                    }}
                    value={value ?? ""}
                    errorMessage={phmfapending ? undefined : errors.phone?.message}
                    label="Phone Number"
                    placeholder={Constant.PHONE_PLACEHOLDER}
                  />
                )}
              />
            </div>
            <div className="row--item">
              <Controller
                name="password_input"
                control={control}
                render={({ field: { onChange } }) => (
                  <PasswordChecker
                    onChange={onChange}
                    checkErrors={ispasswordFieldModified}
                    createLabel="Create Password"
                    setHasPasswordError={setHasPwdError}
                    setBtnVisible={setIsBtnDisabled}
                    createPasswordPlaceholderName="Create password"
                    excludeFromTranslation={false}
                  />
                )}
              />
            </div>
            <Button title="Continue" className="registration__button" disabled={isBtnDisabled || !isPhoneValid} />
          </Form>
          {featureFlag?.splitConsentEnabled && <Consent text={getPrivacyAndTermsOfUseConsent()} />}
        </div>
        {isDesktop && (
          <div className="registration__information">
            {isNativeAppDownloadCtaEnabled
              ? appDownloadPanel
              : renderDesktopLoPanel(loanOfficer, isLoadingLO, handleLOPhoneClick, handleLOEmailClick, () => setLoHeadshotLoadError(true))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* ADO-535683: show bilingual Spanish acknowledgment modal before registration form */}
      {showSpanishModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleSpanishAcknowledge}
          onCancel={handleSpanishCancel}
        />
      )}
      {/* NAF_Lead Portal_Static_6.4.25 Tracking Pixel */}
      {showPixel && (
        <img
          height="1"
          width="1"
          style={{ borderStyle: "none" }}
          alt=""
          src={getPixelSrc(leadProviderName || decryptedUserDetails?.lpn, decryptedUserDetails?.id)}
        />
      )}

      {(isLoading || isFetching) && <LoadingOverlay text="Loading user details..." />}
      {isNativeAppDownloadCtaEnabled ? (
        <AuthSplitLayout>{registrationPage}</AuthSplitLayout>
      ) : (
        registrationPage
      )}
      {isNativeAppDownloadCtaEnabled && !isDesktop && appDownloadPanel && (
        <div className="registration__mobile-app-download">{appDownloadPanel}</div>
      )}
      {!featureFlag?.splitConsentEnabled && <RegistrationLegalConsent />}
      <div className="registration__footer-section">
        <Footer />
      </div>
    </>
  );
}
